import { MdOutlineContentCopy, MdCheck } from "react-icons/md";
import { ReactNode } from "react";
import type { MessageDescriptor } from "@lingui/core";
import { useClipboard } from "../../utils/hooks/useClipboard";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { Discount } from "../../utils/hooks/useCoupons";
import { CouponUseModal } from "./CouponUseModal";
import {
  Card,
  CardContent,
  CardHeader,
  cn,
  Heading,
  HStack,
  VStack,
  Text,
  IconButton,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  CardFooter,
  Callout,
  Flex,
} from "@wootingkb/component-library";
import RegionawareLink, { ReplaceableURL } from "./RegionawareLink";

const COUPON_COPIED_TOOLTIP = msg({
  id: "Coupon_copied_tooltip",
  message: "Copied",
});

const COUPON_PRODUCT_IMAGE_ALT = msg({
  id: "Coupon_product_image_alt",
  message: "Product image",
});

const COUPON_GO_TO_PRODUCT = msg({
  id: "Coupon_go_to_product",
  message: "Go to product",
});

interface Props {
  coupon: Discount;
  content: {
    title: ReactNode;
    image: string;
    imageMobile: string;
    text: ReactNode;
    expires: ReactNode;
    product: ReplaceableURL;
    productLabel?: MessageDescriptor;
    isDisabled?: boolean;
  };
}

export default function CouponCodeItem({ coupon, content }: Props) {
  const { onCopy, hasCopied } = useClipboard(coupon.code);
  const { _: i18n } = useLingui();

  return (
    <Card
      className={cn(
        "border border-outline-primary",
        content.isDisabled &&
          "pointer-events-none cursor-not-allowed opacity-50"
      )}
    >
      <CardHeader className="min-h-12">
        <Heading as="h3" size="h4" weight="semibold">
          {content.title}
        </Heading>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <Flex className="gap-4 lg:flex-row">
          <img
            src={content.image}
            alt={i18n(COUPON_PRODUCT_IMAGE_ALT)}
            className="hidden w-1/3 rounded-default object-cover lg:block"
          />
          <img
            src={content.imageMobile}
            alt={i18n(COUPON_PRODUCT_IMAGE_ALT)}
            className="block w-full rounded-default object-cover lg:hidden"
          />
          {content.text}
        </Flex>
        {content.expires && (
          <Callout
            status="error"
            className="w-full"
            heading={<Trans id="Coupon_expires_label">Expires</Trans>}
            description={content.expires}
          />
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-8">
        <VStack className="gap-2">
          <Text size="sm" weight="semibold">
            <Trans id="Coupon_discount_code_label">Discount code</Trans>
          </Text>
          <HStack
            alignment="centered"
            className="relative cursor-pointer rounded-default border border-dashed border-outline-brand bg-container-brand-primary/40 px-2 py-4"
            onClick={onCopy}
          >
            <Text weight="bold">{coupon.code}</Text>
            <TooltipProvider>
              <Tooltip open={hasCopied} delayDuration={0}>
                <TooltipTrigger asChild>
                  <IconButton
                    className="absolute -top-4 -right-4"
                    variant="secondary"
                    size="lg"
                  >
                    {hasCopied ? <MdCheck /> : <MdOutlineContentCopy />}
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{i18n(COUPON_COPIED_TOOLTIP)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </HStack>
          <CouponUseModal />
        </VStack>
        <RegionawareLink
          href={content.product}
          external
          hideExternalIcon
          variant="button"
          buttonVariant="secondary"
          buttonSize="xs"
          className="self-end px-2 leading-heading"
        >
          {i18n(content.productLabel ?? COUPON_GO_TO_PRODUCT)}
        </RegionawareLink>
      </CardFooter>
    </Card>
  );
}
